import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FiUser, FiMail, FiLock, FiSave, FiAlertCircle, FiCheckCircle, FiShield, FiCamera, FiTrash2, FiBell, FiMoon, FiSun, FiMonitor, FiDroplet, FiPhone } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

export default function Settings() {
  const { user, authFetch, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(user?.email_notifications ?? 1);
  const [otpEnabled, setOtpEnabled] = useState(!!user?.otp_enabled);
  const [otpToggling, setOtpToggling] = useState(false);
  const otpFeatureAvailable = !!user?.otp_feature_enabled;
  const [togglingNotif, setTogglingNotif] = useState(false);
  const [avatarColor, setAvatarColor] = useState(user?.avatar_color || '');
  const [savingColor, setSavingColor] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [savingPhone, setSavingPhone] = useState(false);
  const [dob, setDob] = useState(user?.dob ? String(user.dob).substring(0, 10) : '');
  const [savingDob, setSavingDob] = useState(false);
  // Avatar focal point — both percentages (0-100). Initial state mirrors the
  // saved value so we can detect "any change" for the Save button.
  const [avatarPos, setAvatarPos] = useState({ x: 50, y: 50 });
  const [avatarPosInitial, setAvatarPosInitial] = useState({ x: 50, y: 50 });
  const [reposOpen, setReposOpen] = useState(false);
  const [savingPos, setSavingPos] = useState(false);
  // Initial form values for dirty-detection — Save stays disabled until any
  // field actually differs from what the server has.
  const [initialForm, setInitialForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const fileInputRef = useRef(null);
  const isAdUser = user?.auth_type === 'ad';

  React.useEffect(() => {
    authFetch(`/api/users/${user?.id}/profile`).then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.phone_number) setPhoneNumber(data.phone_number);
        if (data?.dob) setDob(String(data.dob).substring(0, 10));
        if (data) {
          const px = data.avatar_pos_x != null ? parseFloat(data.avatar_pos_x) : 50;
          const py = data.avatar_pos_y != null ? parseFloat(data.avatar_pos_y) : 50;
          setAvatarPos({ x: px, y: py });
          setAvatarPosInitial({ x: px, y: py });
        }
      }).catch(() => {});
  }, []);

  // Save the avatar focal point. The Reposition panel uses this directly so
  // the user gets immediate feedback rather than a deferred "Save" click.
  const handleSaveAvatarPosition = async () => {
    setSavingPos(true);
    try {
      const res = await authFetch('/api/users/profile/avatar-position', {
        method: 'PATCH', body: JSON.stringify({ x: avatarPos.x, y: avatarPos.y })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save position');
      setAvatarPosInitial({ x: avatarPos.x, y: avatarPos.y });
      flashAvatar('ok', 'Photo position updated');
      setReposOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save photo position');
    }
    setSavingPos(false);
  };

  // Form dirtiness — Save button is disabled until something actually changes.
  const formDirty = (
    form.name !== initialForm.name ||
    form.email !== initialForm.email ||
    !!passwords.newPassword ||
    !!passwords.currentPassword ||
    !!passwords.confirmPassword
  );

  const AVATAR_COLORS = [
    { value: '', label: 'Default', color: 'linear-gradient(135deg, #2563eb, #3b82f6)' },
    { value: '#2563eb', label: 'Blue', color: '#2563eb' },
    { value: '#7c3aed', label: 'Purple', color: '#7c3aed' },
    { value: '#db2777', label: 'Pink', color: '#db2777' },
    { value: '#dc2626', label: 'Red', color: '#dc2626' },
    { value: '#ea580c', label: 'Orange', color: '#ea580c' },
    { value: '#d97706', label: 'Amber', color: '#d97706' },
    { value: '#16a34a', label: 'Green', color: '#16a34a' },
    { value: '#0d9488', label: 'Teal', color: '#0d9488' },
    { value: '#0284c7', label: 'Sky', color: '#0284c7' },
    { value: '#4f46e5', label: 'Indigo', color: '#4f46e5' },
    { value: '#64748b', label: 'Slate', color: '#64748b' },
  ];

  const handleSaveAvatarColor = async (color) => {
    setAvatarColor(color);
    setSavingColor(true);
    try {
      const res = await authFetch('/api/users/profile/avatar-color', {
        method: 'PATCH', body: JSON.stringify({ avatar_color: color || null })
      });
      if (res.ok) {
        updateUser({ ...user, avatar_color: color || null });
      }
    } catch (e) { /* ignore */ }
    setSavingColor(false);
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px 12px 42px', borderRadius: 10,
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: 14, outline: 'none'
  };

  // Avatar-section feedback — kept separate from the page-bottom `success`
  // so the message renders next to the avatar UI where the action happened.
  const [avatarMsg, setAvatarMsg] = useState(null); // { type: 'ok'|'err', text }
  const flashAvatar = (type, text) => {
    setAvatarMsg({ type, text });
    setTimeout(() => setAvatarMsg(curr => (curr && curr.text === text ? null : curr)), 4000);
  };
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [pickingSystem, setPickingSystem] = useState(null); // seed currently being applied

  // 20 illustrated character avatars from DiceBear (free, CORS-enabled).
  // The "personas" + "lorelei" mix gives a good range of looks. Clicking one
  // fetches the SVG and uploads it via the normal avatar endpoint, so every
  // existing render site keeps working with no special-casing.
  const SYSTEM_AVATARS = [
    ...Array.from({ length: 10 }, (_, i) => ({ id: `p${i + 1}`, style: 'personas', seed: `al-p-${i + 1}` })),
    ...Array.from({ length: 10 }, (_, i) => ({ id: `l${i + 1}`, style: 'lorelei',  seed: `al-l-${i + 1}` })),
  ].map(a => ({ ...a, url: `https://api.dicebear.com/9.x/${a.style}/svg?seed=${a.seed}` }));

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { flashAvatar('err', 'Please select an image file'); return; }
    if (file.size > 4 * 1024 * 1024) { flashAvatar('err', 'Avatar must be under 4MB'); return; }
    setAvatarUploading(true); setAvatarMsg(null);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await authFetch(`/api/users/${user.id}/avatar`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      updateUser({ ...user, avatar: data.avatar });
      flashAvatar('ok', 'Avatar updated');
    } catch (err) {
      flashAvatar('err', err.message || 'Failed to upload avatar');
    }
    setAvatarUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAvatarRemove = async () => {
    setAvatarUploading(true); setAvatarMsg(null);
    try {
      const res = await authFetch(`/api/users/${user.id}/avatar`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      updateUser({ ...user, avatar: null });
      flashAvatar('ok', 'Avatar removed');
    } catch (err) {
      flashAvatar('err', err.message || 'Failed to remove avatar');
    }
    setAvatarUploading(false);
  };

  const handlePickSystemAvatar = async (av) => {
    setPickingSystem(av.id); setAvatarMsg(null);
    try {
      const r = await fetch(av.url);
      if (!r.ok) throw new Error('Could not fetch avatar image');
      const blob = await r.blob();
      const formData = new FormData();
      formData.append('avatar', new File([blob], `system-${av.seed}.svg`, { type: blob.type || 'image/svg+xml' }));
      const res = await authFetch(`/api/users/${user.id}/avatar`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      updateUser({ ...user, avatar: data.avatar });
      flashAvatar('ok', 'Avatar updated');
      setGalleryOpen(false);
    } catch (err) {
      flashAvatar('err', err.message || 'Could not apply avatar');
    }
    setPickingSystem(null);
  };

  const handleToggleOtp = async () => {
    if (!otpFeatureAvailable) return;
    setOtpToggling(true);
    try {
      const res = await authFetch('/api/users/profile/otp', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !otpEnabled }),
      });
      if (res.ok) {
        const data = await res.json();
        setOtpEnabled(!!data.otp_enabled);
        updateUser({ ...user, otp_enabled: !!data.otp_enabled });
      }
    } catch {}
    setOtpToggling(false);
  };

  const handleToggleNotifications = async () => {
    setTogglingNotif(true);
    try {
      const res = await authFetch('/api/users/profile/notifications', { method: 'PATCH' });
      if (res.ok) {
        const data = await res.json();
        setEmailNotifications(data.email_notifications);
        updateUser({ ...user, email_notifications: data.email_notifications });
      }
    } catch (e) { /* ignore */ }
    setTogglingNotif(false);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');

    const payload = { name: form.name, email: form.email };

    if (passwords.newPassword) {
      if (passwords.newPassword !== passwords.confirmPassword) {
        setError('New passwords do not match');
        setSaving(false);
        return;
      }
      if (passwords.newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        setSaving(false);
        return;
      }
      if (!passwords.currentPassword) {
        setError('Current password is required to change password');
        setSaving(false);
        return;
      }
      payload.currentPassword = passwords.currentPassword;
      payload.newPassword = passwords.newPassword;
    }

    try {
      const res = await authFetch('/api/users/profile/me', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to update profile');
      } else {
        updateUser({ ...user, name: data.name, email: data.email });
        setForm({ name: data.name, email: data.email });
        setInitialForm({ name: data.name, email: data.email });
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSuccess('Profile updated successfully');
      }
    } catch (err) {
      setError('Failed to update profile');
    }
    setSaving(false);
  };

  const themeOptions = [
    { value: 'light', icon: FiSun, label: 'Light' },
    { value: 'dark', icon: FiMoon, label: 'Dark' },
    { value: 'system', icon: FiMonitor, label: 'System' },
  ];

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 800, marginBottom: 4, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>Account Settings</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>Manage your profile information and security</p>
      </motion.div>

      {/* Avatar Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiCamera size={18} color="var(--accent)" />
          </div>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Profile Photo</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Upload one of your own (max 4&nbsp;MB · JPG, PNG, GIF or SVG) or pick from the gallery</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {user?.avatar ? (
            <img src={`/api/users/avatar/${user.avatar}`} alt="Avatar"
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', objectPosition: `${avatarPos.x}% ${avatarPos.y}%`, border: '3px solid var(--border)' }} />
          ) : (
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: avatarColor || 'linear-gradient(135deg, var(--accent), #0ea5e9)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid var(--border)' }}>
              <span style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>{(() => { const p = user?.name?.trim().split(/\s+/) || []; return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : (p[0]?.[0]?.toUpperCase() || '?'); })()}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
            <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              disabled={avatarUploading} onClick={() => fileInputRef.current?.click()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--accent)', background: 'rgba(37,99,235,0.06)', color: 'var(--accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: avatarUploading ? 0.6 : 1 }}>
              <FiCamera size={14} /> {avatarUploading ? 'Uploading...' : 'Upload Photo'}
            </motion.button>
            <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              disabled={avatarUploading} onClick={() => setGalleryOpen(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <FiCamera size={14} /> {galleryOpen ? 'Close gallery' : 'Choose from gallery'}
            </motion.button>
            {user?.avatar && (
              <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                disabled={avatarUploading} onClick={() => setReposOpen(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <FiCamera size={14} /> {reposOpen ? 'Cancel' : 'Reposition'}
              </motion.button>
            )}
            {user?.avatar && (
              <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                disabled={avatarUploading} onClick={handleAvatarRemove}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: avatarUploading ? 0.6 : 1 }}>
                <FiTrash2 size={14} /> Remove
              </motion.button>
            )}
          </div>
        </div>

        {avatarMsg && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8,
            background: avatarMsg.type === 'ok' ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.06)',
            border: `1px solid ${avatarMsg.type === 'ok' ? 'rgba(22,163,74,0.25)' : 'rgba(220,38,38,0.25)'}`,
            color: avatarMsg.type === 'ok' ? '#16a34a' : '#dc2626',
          }}>
            {avatarMsg.type === 'ok'
              ? <FiCheckCircle size={14} style={{ flexShrink: 0 }} />
              : <FiAlertCircle size={14} style={{ flexShrink: 0 }} />} {avatarMsg.text}
          </div>
        )}

        {galleryOpen && (
          <div style={{ marginTop: 18, padding: 16, borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>Pick an avatar</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>Twenty illustrated characters — click one to set it as your photo.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 10 }}>
              {SYSTEM_AVATARS.map(av => (
                <button key={av.id} type="button"
                  onClick={() => handlePickSystemAvatar(av)}
                  disabled={!!pickingSystem}
                  title={`Use this avatar (${av.style})`}
                  style={{
                    position: 'relative', padding: 0, border: '2px solid var(--border)', borderRadius: '50%',
                    width: 72, height: 72, background: 'var(--bg-card)', cursor: pickingSystem ? 'wait' : 'pointer',
                    overflow: 'hidden', transition: 'transform 0.15s, border-color 0.15s',
                    opacity: pickingSystem && pickingSystem !== av.id ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { if (!pickingSystem) { e.currentTarget.style.transform = 'scale(1.06)'; e.currentTarget.style.borderColor = 'var(--accent)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                  <img src={av.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                  {pickingSystem === av.id && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700 }}>…</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {reposOpen && user?.avatar && (() => {
          const dirtyPos = avatarPos.x !== avatarPosInitial.x || avatarPos.y !== avatarPosInitial.y;
          const onDrag = (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const point = e.touches ? e.touches[0] : e;
            const x = Math.max(0, Math.min(100, ((point.clientX - rect.left) / rect.width) * 100));
            const y = Math.max(0, Math.min(100, ((point.clientY - rect.top) / rect.height) * 100));
            setAvatarPos({ x, y });
          };
          return (
            <div style={{ marginTop: 18, padding: 16, borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Drag the dot on the photo below to set what's centred in your avatar — no re-upload needed.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                {/* Wide draggable preview */}
                <div
                  onMouseDown={(e) => { onDrag(e); const move = (ev) => onDrag({ ...ev, currentTarget: e.currentTarget }); const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); }; window.addEventListener('mousemove', move); window.addEventListener('mouseup', up); }}
                  onTouchStart={(e) => onDrag(e)}
                  onTouchMove={(e) => onDrag(e)}
                  style={{
                    position: 'relative', width: 240, height: 160, borderRadius: 12,
                    backgroundImage: `url(/api/users/avatar/${user.avatar})`,
                    backgroundSize: 'cover',
                    backgroundPosition: `${avatarPos.x}% ${avatarPos.y}%`,
                    border: '2px solid var(--border)', cursor: 'crosshair', userSelect: 'none', flexShrink: 0,
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    left: `calc(${avatarPos.x}% - 10px)`, top: `calc(${avatarPos.y}% - 10px)`,
                    width: 20, height: 20, borderRadius: '50%',
                    background: '#fff', border: '3px solid var(--accent)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.4)', pointerEvents: 'none',
                  }} />
                </div>
                {/* Live circle preview at the size it'll appear */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <img src={`/api/users/avatar/${user.avatar}`} alt="Preview"
                    style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', objectPosition: `${avatarPos.x}% ${avatarPos.y}%`, border: '3px solid var(--accent)' }} />
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Preview</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setAvatarPos({ x: 50, y: 50 })}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Reset to centre
                </button>
                <button type="button" onClick={handleSaveAvatarPosition} disabled={!dirtyPos || savingPos}
                  style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: (!dirtyPos || savingPos) ? '#94a3b8' : 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: (!dirtyPos || savingPos) ? 'not-allowed' : 'pointer' }}>
                  {savingPos ? 'Saving…' : 'Save position'}
                </button>
              </div>
            </div>
          );
        })()}
      </motion.div>

      {/* Avatar Color */}
      {!user?.avatar && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiDroplet size={18} color="var(--accent)" />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Avatar Color</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Choose a color for your initials avatar</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {AVATAR_COLORS.map(ac => (
              <motion.button
                key={ac.value}
                type="button"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleSaveAvatarColor(ac.value)}
                disabled={savingColor}
                style={{
                  width: 40, height: 40, borderRadius: '50%', border: avatarColor === ac.value ? '3px solid var(--accent)' : '2px solid var(--border)',
                  background: ac.color, cursor: 'pointer', position: 'relative',
                  boxShadow: avatarColor === ac.value ? '0 0 0 2px var(--bg-card), 0 0 0 4px var(--accent)' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                title={ac.label}
              >
                {avatarColor === ac.value && (
                  <span style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>✓</span>
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>
      )}

      <form onSubmit={handleSaveProfile}>
        {/* Profile Information */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiUser size={18} color="var(--accent)" />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Profile Information</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isAdUser ? 'Name and email are managed by Active Directory' : 'Update your name and email address'}</p>
            </div>
          </div>

          {isAdUser && (
            <div style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #bae6fd', background: '#f0f9ff', fontSize: 12, color: '#0369a1', marginBottom: 14 }}>
              Your name, email, and password are managed by Active Directory and cannot be changed here.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Full Name</label>
              <div style={{ position: 'relative' }}>
                <FiUser size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
                  disabled={isAdUser}
                  placeholder="Your full name" style={{ ...inputStyle, ...(isAdUser ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Email Address</label>
              <div style={{ position: 'relative' }}>
                <FiMail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                  disabled={isAdUser}
                  placeholder="your@email.com" style={{ ...inputStyle, ...(isAdUser ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Phone Number</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <FiPhone size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input type="tel" value={phoneNumber}
                    disabled={isAdUser}
                    onChange={e => {
                      let digits = e.target.value.replace(/[^\d]/g, '').slice(0, 11);
                      if (!digits) { setPhoneNumber(''); return; }
                      let formatted = '+';
                      if (digits.length <= 2) { formatted += digits; }
                      else {
                        formatted += digits.slice(0, 2);
                        let rest = digits.slice(2);
                        for (let i = 0; i < rest.length; i += 3) {
                          formatted += ' ' + rest.slice(i, i + 3);
                        }
                      }
                      setPhoneNumber(formatted);
                    }}
                    placeholder="+61 455 112 578" style={{ ...inputStyle, ...(isAdUser ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                </div>
                <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  disabled={savingPhone || isAdUser}
                  onClick={async () => {
                    setSavingPhone(true);
                    try {
                      const res = await authFetch('/api/users/profile/phone', { method: 'PATCH', body: JSON.stringify({ phone_number: phoneNumber || null }) });
                      if (res.ok) setSuccess('Phone number updated');
                      else setError('Failed to update phone number');
                    } catch (e) { setError('Failed to update phone number'); }
                    setSavingPhone(false);
                  }}
                  style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: isAdUser ? '#94a3b8' : 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: (savingPhone || isAdUser) ? 'not-allowed' : 'pointer', opacity: (savingPhone || isAdUser) ? 0.5 : 1, flexShrink: 0 }}>
                  {savingPhone ? 'Saving...' : 'Save'}
                </motion.button>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Include country code (e.g. +61 455 112 578). Spaces are added automatically.</div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Date of Birth</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" value={dob}
                  onChange={e => setDob(e.target.value)}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0]}
                  min={new Date(new Date().setFullYear(new Date().getFullYear() - 120)).toISOString().split('T')[0]}
                  style={{ ...inputStyle, flex: 1, minHeight: 44 }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  disabled={savingDob}
                  onClick={async () => {
                    setSavingDob(true);
                    setError(''); setSuccess('');
                    try {
                      const res = await authFetch('/api/users/profile/dob', { method: 'PATCH', body: JSON.stringify({ dob: dob || null }) });
                      if (res.ok) { setSuccess('Date of birth updated'); updateUser({ ...user, dob: dob || null }); }
                      else { const d = await res.json().catch(() => ({})); setError(d.error || 'Failed to update date of birth'); }
                    } catch (e) { setError('Failed to update date of birth'); }
                    setSavingDob(false);
                  }}
                  style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: savingDob ? 'not-allowed' : 'pointer', opacity: savingDob ? 0.5 : 1, flexShrink: 0 }}>
                  {savingDob ? 'Saving...' : 'Save'}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Change Password - hidden for AD users */}
        {!isAdUser && (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiShield size={18} color="var(--accent)" />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Change Password</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Leave blank to keep your current password</p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Current Password</label>
              <div style={{ position: 'relative' }}>
                <FiLock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input type="password" value={passwords.currentPassword}
                  onChange={e => setPasswords(p => ({ ...p, currentPassword: e.target.value }))}
                  placeholder="Enter current password" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <FiLock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input type="password" value={passwords.newPassword}
                    onChange={e => setPasswords(p => ({ ...p, newPassword: e.target.value }))}
                    placeholder="Min 6 characters" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <FiLock size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input type="password" value={passwords.confirmPassword}
                    onChange={e => setPasswords(p => ({ ...p, confirmPassword: e.target.value }))}
                    placeholder="Re-enter new password" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                </div>
              </div>
            </div>
          </div>
        </motion.div>)}

        {/* Email Notifications */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiBell size={18} color="var(--accent)" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Email Notifications</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>Receive alerts for assignments, completions, and overdue work orders</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {emailNotifications ? 'Notifications enabled' : 'Notifications disabled'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {emailNotifications ? 'You will receive email alerts' : 'You will not receive email alerts'}
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={handleToggleNotifications}
              disabled={togglingNotif}
              type="button"
              style={{
                width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                background: emailNotifications ? '#2563eb' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0
              }}
            >
              <div style={{
                width: 22, height: 22, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: emailNotifications ? 27 : 3,
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </motion.button>
          </div>
        </motion.div>

        {/* Two-Step Login (email OTP) */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.27 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiShield size={18} color="var(--accent)" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Two-Step Login</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                Add a 4-digit email code as a second factor when you sign in.
              </p>
            </div>
          </div>
          {!otpFeatureAvailable ? (
            <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-input)', border: '1px dashed var(--border)', color: 'var(--text-muted)', fontSize: 12 }}>
              Two-step login isn't available on this workspace yet. Ask your admin to enable it under Admin Panel &rarr; Settings.
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 14 }}>
                {[
                  { icon: FiLock, title: 'Stronger security', body: 'Your password alone won’t let anyone in — they also need access to your inbox.' },
                  { icon: FiMail, title: 'Nothing to install', body: 'Codes arrive by email, so there’s no extra app to set up or carry around.' },
                  { icon: FiAlertCircle, title: 'Brute-force protected', body: 'Wrong codes lock the account for 60 minutes after 3 attempts.' },
                ].map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <div key={i} style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Icon size={12} color="#2563eb" />
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{b.title}</div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{b.body}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {otpEnabled ? 'Two-step login is on' : 'Two-step login is off'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {otpEnabled ? 'We’ll email you a 4-digit code every time you sign in.' : 'You’ll sign in with just your password.'}
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={handleToggleOtp} disabled={otpToggling} type="button"
                  style={{
                    width: 52, height: 28, borderRadius: 14, border: 'none', cursor: otpToggling ? 'not-allowed' : 'pointer',
                    background: otpEnabled ? '#2563eb' : 'var(--border)',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 3,
                    left: otpEnabled ? 27 : 3,
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </motion.button>
              </div>
            </>
          )}
        </motion.div>

        {/* Appearance */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiMoon size={18} color="var(--accent)" />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Appearance</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Choose your preferred theme</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {themeOptions.map(opt => (
              <motion.button
                key={opt.value}
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setTheme(opt.value)}
                style={{
                  flex: 1, padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                  border: theme === opt.value ? '2px solid var(--accent)' : '1px solid var(--border)',
                  background: theme === opt.value ? 'rgba(37,99,235,0.08)' : 'var(--bg-input)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  transition: 'all 0.2s'
                }}
              >
                <opt.icon size={20} color={theme === opt.value ? 'var(--accent)' : 'var(--text-secondary)'} />
                <span style={{
                  fontSize: 13, fontWeight: theme === opt.value ? 700 : 500,
                  color: theme === opt.value ? 'var(--accent)' : 'var(--text-secondary)'
                }}>{opt.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>



        {/* Messages */}
        {error && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 16, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertCircle size={16} /> {error}
          </motion.div>
        )}

        {success && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 16, background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)', color: '#16a34a', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiCheckCircle size={16} /> {success}
          </motion.div>
        )}

        <motion.button type="submit" disabled={saving || !formDirty}
          whileHover={!formDirty ? {} : { scale: 1.02 }} whileTap={!formDirty ? {} : { scale: 0.98 }}
          style={{
            width: '100%', padding: '14px', borderRadius: 10, border: 'none',
            background: !formDirty ? '#94a3b8' : 'var(--accent)', color: '#fff', fontSize: 15, fontWeight: 600,
            cursor: (saving || !formDirty) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: !formDirty ? 'none' : '0 4px 12px rgba(37,99,235,0.3)',
            opacity: (saving || !formDirty) ? 0.7 : 1
          }}>
          <FiSave size={16} /> {saving ? 'Saving...' : formDirty ? 'Save Changes' : 'No changes to save'}
        </motion.button>
      </form>
    </div>
  );
}
